import { computed } from 'vue';
import { useProduct } from '@modules/product';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import { useUpc } from '@modules/upc';

import { useTraits } from '@composables/useTraits';

export const SELLER_NAME_MAX_CHARS = 30;

/**
 * Formats the seller name
 * @param {string} value
 * @returns {string}
 */
export function formatter(value) {
  return value.slice(0, SELLER_NAME_MAX_CHARS).replace(/"/g, '');
}

export function useMarketPlace() {
  // Get Product Id
  const { productId, productData } = useProduct();

  // Get Product Flags
  const { isMasterProductFlag } = useProductFlags();
  const { marketPlaceItemEnabled } = useFeatureEligibility();

  // Get Selected UPC
  const { selectedUpcId, upcData } = useUpc(productId);

  const { selectedColorId } = useTraits(productId);

  // Compute Seller Name
  const sellerName = computed(() => {
    if (
      !isMasterProductFlag
      && marketPlaceItemEnabled
      && selectedUpcId.value !== null
    ) {
      if (
        upcData.value.upcs[selectedUpcId.value]?.marketplaceFlag
        && upcData.value.upcs[selectedUpcId.value]?.seller?.sellerName
        && upcData.value.upcs[selectedUpcId.value]?.availability.available
      ) {
        return formatter(upcData.value
          .upcs[selectedUpcId.value]
          .seller
          .sellerName);
      }
      return '';
    }

    return '';
  });

  // Compute Seller Id
  const sellerId = computed(() => {
    if (
      !isMasterProductFlag
      && marketPlaceItemEnabled
      && selectedUpcId.value !== null
    ) {
      if (
        upcData.value.upcs[selectedUpcId.value]?.marketplaceFlag
        && upcData.value.upcs[selectedUpcId.value]?.availability.available
      ) {
        return upcData.value.upcs[selectedUpcId.value]?.seller?.sellerId;
      }
      return null;
    }

    return null;
  });

  // used only once for PDP landing page for Marketplace prods when traits selection is incomplete
  const getInitSellerName = () => {
    if (
      !isMasterProductFlag
        && marketPlaceItemEnabled
        && selectedColorId?.value
    ) {
      const selectedSize = '0'; // auto select the size
      const defaultUpcId = upcData.value.upcMap[`${selectedColorId?.value}_${selectedSize}`];
      if (
        upcData.value.upcs[defaultUpcId]?.marketplaceFlag
          && upcData.value.upcs[defaultUpcId]?.seller?.sellerName
          && upcData.value.upcs[defaultUpcId]?.availability.available
      ) {
        return formatter(upcData.value
          .upcs[defaultUpcId]
          .seller
          .sellerName);
      }
      return null;
    }

    return null;
  };

  // used only once for PDP landing page for Marketplace prods when traits selection is incomplete
  const getInitSellerId = () => {
    if (
      !isMasterProductFlag
        && marketPlaceItemEnabled
        && selectedColorId?.value
    ) {
      const selectedSize = '0'; // auto select the size
      const defaultUpcId = upcData.value.upcMap[`${selectedColorId?.value}_${selectedSize}`];
      if (
        upcData.value.upcs[defaultUpcId]?.marketplaceFlag
          && upcData.value.upcs[defaultUpcId]?.availability.available
      ) {
        return upcData.value.upcs[defaultUpcId]?.seller?.sellerId;
      }
      return null;
    }

    return null;
  };

  const hasSplHandlingAttrbs = computed(() => {
    const splHandlingAttrbsArray = productData?.relationships?.upcs?.[selectedUpcId.value]?.splHandlingAttrbs;
    return Array.isArray(splHandlingAttrbsArray) && splHandlingAttrbsArray.length > 0;
  });

  return {
    sellerName,
    sellerId,
    getInitSellerId,
    getInitSellerName,
    hasSplHandlingAttrbs,
  };
}

export default useMarketPlace;
